import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import DatePicker from "react-datepicker";
import { ro } from "date-fns/locale";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./EditSuppliersContract.css";
import axiosInstance from "../../../Config/axiosInstance";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Înregistrăm localizarea pentru react-datepicker
registerLocale("ro", ro);

const EditSupplierContract = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // preluăm id-ul din URL
  
  // State-uri pentru DatePicker
  const [contractDate, setContractDate] = useState(null);
  const [expireDate, setExpireDate] = useState(null);
  
  const [formData, setFormData] = useState({
    contract_number: "",
    contract_date: "",
    expire_date: "",
    provider_contract_nr: "",
    contract_description: "",
    alert_days: "",
    status: "",
    supplier: "",
    remarks: "",
  });

  const [suppliers, setSuppliers] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const animatedComponents = makeAnimated();

  // Funcție pentru gestionarea datelor din DatePicker
  const handleDateChange = (date, fieldName) => {
    // Convertim data în format YYYY-MM-DD pentru backend
    const isoDate = date ? date.toISOString().split("T")[0] : "";
    
    setFormData(prev => ({
      ...prev,
      [fieldName]: isoDate
    }));

    // Actualizăm și state-urile pentru DatePicker
    if (fieldName === "contract_date") {
      setContractDate(date);
    } else if (fieldName === "expire_date") {
      setExpireDate(date);
    }
  };

  //Functie de actualizare a valorilor din formular
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  //Selectarea furnizorilor
  const handleSupplierChange = (selectedOption) => {
    setFormData({ ...formData, supplier: selectedOption ? selectedOption.value : "" });
  };

  //Actualizarea datelor 
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosInstance.put(`/supplier-contracts/${id}/`, formData); // Folosim PUT pentru a actualiza contractul
      console.log("Supplier contract updated successfully:", response.data);
      navigate(-1); // Navigăm înapoi la pagina anterioară
    } catch (error) {
      console.error("Error updating supplier contract:", error.response?.data || error.message);
    }
  };

  // Preluarea datelor contractului pentru editare
  useEffect(() => {
    axiosInstance
      .get(`/supplier-contracts/${id}/`)
      .then((response) => {
        const data = response.data;
        setFormData({
          contract_number: data.contract_number,
          contract_date: data.contract_date,
          expire_date: data.expire_date,
          provider_contract_nr: data.provider_contract_nr,
          contract_description: data.contract_description,
          alert_days: data.alert_days,
          status: data.status,
          supplier: data.supplier,
          remarks: data.remarks,
        });
        
        // Setăm datele pentru DatePicker
        if (data.contract_date) {
          setContractDate(new Date(data.contract_date));
        }
        if (data.expire_date) {
          setExpireDate(new Date(data.expire_date));
        }
      })
      .catch((error) => console.error("Error fetching supplier contract:", error));
  }, [id]);

  // Preluare lista de furnizori pentru editare
  useEffect(() => {
    const fetchSuppliersForEdit = async () => {
      try {
        // Luăm departamentele la care are acces userul
        const departmentResponse = await axiosInstance.get("/department/user/");
        const accessibleDepartments = departmentResponse.data.map(dep => dep.value);

        // Luăm statusurile supplierilor și departamentele lor
        const statusSupplierDeptResponse = await axiosInstance.get("/status-supplier-department/");
        const supplierDepartments = statusSupplierDeptResponse.data;

        // Selectăm doar supplierii activi din departamentele accesibile
        const allowedSupplierIds = supplierDepartments
          .filter(item =>
            item.status === "Active" &&
            accessibleDepartments.includes(item.department)
          )
          .map(item => item.supplier);

        // Luăm toți supplierii
        const suppliersResponse = await axiosInstance.get("/suppliers/");
        let filteredSuppliers = suppliersResponse.data.filter(supplier =>
          allowedSupplierIds.includes(supplier.id)
        );

        // Adăugăm supplierul curent din contract dacă nu este în lista filtrată
        if (formData.supplier) {
          const currentSupplier = suppliersResponse.data.find(
            s => s.id === formData.supplier
          );
          if (currentSupplier && !filteredSuppliers.some(s => s.id === currentSupplier.id)) {
            filteredSuppliers = [...filteredSuppliers, currentSupplier];
          }
        }

        // Transformăm în opțiuni pentru dropdown
        const supplierOptions = filteredSuppliers.map(supplier => ({
          value: supplier.id,
          label: supplier.name,
        }));

        setSuppliers(supplierOptions);
      } catch (error) {
        console.error("Eroare la preluarea listei de furnizori (edit):", error);
      }
    };

    fetchSuppliersForEdit();
  }, [formData.supplier]);


  // Preluăm opțiunile pentru status
  useEffect(() => {
    axiosInstance
      .get("/status-choices-supplier/")
      .then((response) => {
        const options = response.data.status_choices.map(([value, label]) => ({
          value,
          label,
        }));
        setStatusOptions(options);
      })
      .catch((error) => console.error("Error fetching status choices:", error));
  }, []);

  return (
    <div className="edit-supplier-contract-page">
      <div className="edit-supplier-container">
        <div className="supplier-header">
          <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
          <h2 className="edit-supplier-title">Edit Supplier Contract</h2>
        </div>
      </div>
      <hr className="title-separator" />
      <div className="edit-supplier-container">
        <form onSubmit={handleSubmit} className="edit-supplier-form">
          <div className="form-group">
            <label htmlFor="contract_number" className="label">PMPC Contract Nr. <span style={{ color: 'red' }}>*</span></label>
            <input
              id="contract_number"
              type="text"
              name="contract_number"
              value={formData.contract_number}
              onChange={handleChange}
              className="input"
              disabled
            />
          </div>
          <div className="form-group-row">
            <div className="form-group">
              <label htmlFor="contract_date" className="label">Contract Date <span style={{ color: 'red' }}>*</span></label>
              <div className="datepicker-container addsuppliers-datepicker">
                <DatePicker
                  selected={contractDate}
                  onChange={(date) => handleDateChange(date, "contract_date")}
                  dateFormat="dd.MM.yyyy"
                  locale="ro"
                  placeholderText="dd.mm.yyyy"
                  className="datepicker-input"
                  required
                  wrapperClassName="datepicker-wrapper"
                />
                <span className="calendar-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </span>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="expire_date" className="label">Finish Contract Date <span style={{ color: 'red' }}>*</span></label>
              <div className="datepicker-container addsuppliers-datepicker">
                <DatePicker
                  selected={expireDate}
                  onChange={(date) => handleDateChange(date, "expire_date")}
                  dateFormat="dd.MM.yyyy"
                  locale="ro"
                  placeholderText="dd.mm.yyyy"
                  className="datepicker-input"
                  required
                  wrapperClassName="datepicker-wrapper"
                />
                <span className="calendar-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="provider_contract_nr" className="label">Provider Contract - Number & Date <span style={{ color: 'red' }}>*</span></label>
            <input
              id="provider_contract_nr"
              type="text"
              name="provider_contract_nr"
              value={formData.provider_contract_nr}
              onChange={handleChange}
              className="input"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="contract_description" className="label">Contract Description <span style={{ color: 'red' }}>*</span></label>
            <textarea
              id="contract_description"
              name="contract_description"
              value={formData.contract_description}
              onChange={handleChange}
              className="textarea"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="supplier" className="label">Supplier <span style={{ color: 'red' }}>*</span></label>
            <Select
              id="supplier"
              name="supplier"
              classNamePrefix="react-select"
              options={suppliers}
              value={suppliers.find((s) => s.value === formData.supplier) || null}
              onChange={handleSupplierChange}
              components={animatedComponents}
              styles={{
                control: (provided) => ({
                  ...provided,
                  borderRadius: "8px",
                  minHeight: "34px",
                }),
              }}
            />
          </div>

          <div className="form-group-row">
            <div className="form-group">
              <label htmlFor="alert_days" className="label">Alert Days</label>
              <input
                id="alert_days"
                type="number"
                name="alert_days"
                value={formData.alert_days}
                onChange={handleChange}
                className="input"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="status" className="label">Status <span style={{ color: 'red' }}>*</span></label>
              <Select
                id="status"
                name="status"
                classNamePrefix="react-select"
                options={statusOptions}
                value={statusOptions.find((s) => s.value === formData.status) || null}
                onChange={(selectedOption) => setFormData({ ...formData, status: selectedOption.value })}
                components={animatedComponents}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderRadius: "8px",
                    minHeight: "34px",
                  }),
                }}
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="remarks" className="label">Remarks</label>
            <textarea
              id="remarks"
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              className="textarea"
            />
          </div>
          <div className="form-group-row">
            <div className="button-group-inline">
              <button type="button" className="button discard" onClick={() => navigate(-1)}>
                Discard
              </button>
              <button type="submit" className="button save">Save</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSupplierContract;